import type { AppliedModifier } from "~/types";
import { scaleModifiersToLine } from "~/utils/lineModifierQuantities";

/**
 * Cart-line identity, in two flavours. Both compare the same modifier ids and the same
 * `split_quantity`; they differ only in how they treat the quantity, because there are two different
 * questions being asked — "same KIND of line?" (merge) and "find THIS row" (lookup). Note the two
 * comparators exist because rescaling rounds, not because those questions need different code: see
 * `isSameStoredModifierSet` below.
 *
 * Neither compares the raw stored `quantity` as a proxy for "same line". Under the line-total contract
 * `quantity` is `picked x lineQuantity`, so comparing it directly makes identity depend on line size:
 * the same product + add-on merged when added twice at quantity 1, but forked into a duplicate line
 * when added at 2 and then 1.
 *
 * `split_quantity` IS compared: a split share and a non-split add-on of the same modifier are priced by
 * different rules and must never collapse into one line.
 *
 * KNOWN LIMITATION (both): the comparison is strict, so `undefined !== false`. A line hydrated from a
 * stored order carries no flag (the backend persists `modifier_group_id` but not `split_quantity`),
 * while a fresh pick always carries an explicit boolean — so adding a product that matches a
 * pre-existing stored line produces a SECOND cart line rather than merging. Cosmetic, not financial:
 * both lines price correctly and the order total is right. Accepted over a group -> flag client-side
 * cache, to keep these comparators simple and avoid extra requests.
 */

const compareSets = (
  modifiers1: AppliedModifier[] | undefined,
  modifiers2: AppliedModifier[] | undefined,
  quantitiesMatch: (q1: number, q2: number) => boolean,
): boolean => {
  if (!modifiers1 || !modifiers2) return false;
  if (modifiers1.length !== modifiers2.length) return false;

  const sorted1 = [...modifiers1].sort((a, b) => Number(a.modifier_id) - Number(b.modifier_id));
  const sorted2 = [...modifiers2].sort((a, b) => Number(a.modifier_id) - Number(b.modifier_id));

  return sorted1.every((mod1, index) => {
    const mod2 = sorted2[index]!;
    if (mod1.modifier_id !== mod2.modifier_id) return false;
    if (mod1.split_quantity !== mod2.split_quantity) return false;
    return quantitiesMatch(Number(mod1.quantity ?? 1), Number(mod2.quantity ?? 1));
  });
};

/**
 * "Are these the same KIND of line?" — the MERGE decision.
 *
 * Compares the PER-UNIT portion count, which is the value actually invariant to line size. Ignoring
 * quantity altogether merged "10 units with one portion each" into "10 units with two portions each":
 * the money was conserved, but the distinction was lost and the kitchen could no longer tell which
 * units got which.
 *
 * Both line quantities are REQUIRED, never defaulted — the two sets are routinely scaled against
 * different lines (that is the whole point at a modal commit), so a defaulted line would silently
 * answer a different question. Interleaved with their sets so a call site cannot transpose them
 * unnoticed. Compared by cross-multiplication so both sides stay integers and 2/3 vs 1/3 is exact.
 */
export function areModifiersEqual(
  modifiers1: AppliedModifier[] | undefined,
  lineQuantity1: number,
  modifiers2: AppliedModifier[] | undefined,
  lineQuantity2: number,
): boolean {
  const line1 = Number(lineQuantity1);
  const line2 = Number(lineQuantity2);
  // No usable per-unit reading; fall back to the stored totals.
  if (!(Number.isFinite(line1) && line1 > 0 && Number.isFinite(line2) && line2 > 0)) {
    return compareSets(modifiers1, modifiers2, (q1, q2) => q1 === q2);
  }
  return compareSets(modifiers1, modifiers2, (q1, q2) => q1 * line2 === q2 * line1);
}

/**
 * "Is this the exact same stored set?" — used to LOCATE a row, not to judge whether two picks belong
 * together.
 *
 * Compares the stored line totals verbatim. This second comparator is a FORCED CONSEQUENCE of
 * line-total storage meeting legacy data, not a free-standing design choice — under picked-count
 * storage a single id/quantity compare would cover merge and lookup both, and the line-quantity
 * parameters would not be needed at all. Do not read it as a preference.
 *
 * What forces it: `rescaleModifiersToLine` rounds (50 on a 3-unit line taken to 4 units gives 67, whose
 * per-unit ratio 67/4 does not equal the original 50/3), so judging a row lookup by per-unit would fail
 * to match the row the array came from and append a duplicate instead of replacing it. Exact totals are
 * rounding-proof here precisely because the caller is holding the numbers it is looking for.
 *
 * That rounding is NARROW: anything written under this contract divides exactly
 * (`p*L / L * L' = p*L'`), so only a row whose stored value is not a multiple of its line rounds — a
 * legacy pre-870 row, or one already rounded once, which then compounds.
 *
 * The deeper smell is that a row LOOKUP is identity-by-value at all. A client-side line id would make
 * it rounding-proof by construction and delete this function; that wants its own ticket.
 */
export function isSameStoredModifierSet(
  modifiers1: AppliedModifier[] | undefined,
  modifiers2: AppliedModifier[] | undefined,
): boolean {
  return compareSets(modifiers1, modifiers2, (q1, q2) => q1 === q2);
}

/**
 * "Same modifiers, whatever the amounts?" — quantity-agnostic.
 *
 * Only for `addToCart`'s `mode: "set"`, where the caller is authoritative that it is REPLACING one
 * specific line and has already resolved which. Neither of the comparisons above can be used there: the
 * cart operator hands back an array it has just rescaled, so the stored totals no longer match, and a
 * rounded rescale (50 on a 3-unit line -> 67 on 4) means the per-unit ratio no longer matches either.
 * Asking a weaker question is correct here precisely because the caller already knows the answer.
 */
export function haveSameModifierIds(
  modifiers1: AppliedModifier[] | undefined,
  modifiers2: AppliedModifier[] | undefined,
): boolean {
  return compareSets(modifiers1, modifiers2, () => true);
}

interface CartLineForLookup {
  product: { id: string | number };
  quantity: number;
  appliedModifiers?: AppliedModifier[];
}

/**
 * Resolve which existing cart line a modal's current selection would merge into, if any.
 *
 * `selectedModifiers` is the modal's PICKED state (per-unit counts) and MUST be scaled to
 * `modalQuantity` before comparing — `areModifiersEqual` requires both sides to already be LINE
 * TOTALS, and its cross-multiplication silently breaks whenever `modalQuantity > 1` if handed raw
 * picked counts instead. That break makes the lookup miss on a repeat-add, and a caller that then
 * falls back to a weaker ids-only match can overwrite an existing line's quantity instead of
 * merging into it. This helper exists specifically so no call site has to remember to scale first.
 */
export function findExistingCartLine<T extends CartLineForLookup>(
  cartItems: T[] | undefined,
  product: { id: string | number },
  selectedModifiers: AppliedModifier[] | undefined,
  modalQuantity: number,
): T | undefined {
  const scaled = scaleModifiersToLine(selectedModifiers, modalQuantity);
  return cartItems?.find(
    (item) =>
      item.product.id === product.id &&
      areModifiersEqual(item.appliedModifiers, item.quantity, scaled, modalQuantity),
  );
}

interface CartItemInput {
  product_id?: string | number;
  product?: { id: string | number };
  id?: string | number;
  order_product_id?: string | null;
  quantity?: number;
  amount?: number;
  appliedModifiers?: Array<{
    modifier_id?: string | number;
    id?: string | number;
    quantity?: number;
  }>;
  modifiers?: Array<{
    modifier_id?: string | number;
    id?: string | number;
    quantity?: number;
  }>;
  package_product_type?: string;
  type?: string;
}

interface PreparedItem {
  product_id: string | number | undefined;
  quantity: number;
  appliedModifiers: { modifier_id: string | number; quantity: number }[];
  order_product_id?: string | null;
  package_product_type?: string;
}

export function prepareItemsForUpdate(items: CartItemInput[]): PreparedItem[] {
  return items.map((item) => {
    const modifiers = item.appliedModifiers || item.modifiers || [];
    // Deselected options are DROPPED, never forwarded at quantity 0.
    //
    // `processItemsOrder` charges `price * (mod.quantity || 1)`, so a forwarded 0 is promoted to one
    // charged portion server-side — an add-on nobody ordered, on an order that then settles through
    // refund-then-repay at that wrong total. Stored rows written by the old hub picker do contain
    // explicit zeroes (they persisted unticked options), so this is a live path on order edit, not a
    // theoretical one.
    //
    // `?? 1` inside the filter, not `|| 1`: a MISSING quantity legitimately means one portion per
    // line and must survive, while an explicit 0 must not.
    const formattedModifiers = modifiers
      .filter((m) => (m.quantity ?? 1) > 0)
      .map((m) => ({
        modifier_id: m.modifier_id || m.id || "",
        quantity: m.quantity ?? 1,
      }));
    const productId = item.product_id || item.product?.id || item.id;

    if (item.order_product_id) {
      return {
        order_product_id: item.order_product_id,
        product_id: productId,
        quantity: item.quantity || item.amount || 1,
        appliedModifiers: formattedModifiers,
        ...(item.package_product_type && {
          package_product_type: item.package_product_type,
        }),
        ...(item.type && { package_product_type: item.type }),
      };
    }

    if (!productId) {
      console.warn("New item without product_id:", item);
    }

    return {
      product_id: productId,
      quantity: item.quantity || item.amount || 1,
      appliedModifiers: formattedModifiers,
      ...(item.package_product_type && {
        package_product_type: item.package_product_type,
      }),
      ...(item.type && { package_product_type: item.type }),
    };
  });
}
